/* ============================================================================
   SFX — synthesized, zero assets
   ----------------------------------------------------------------------------
   Every sound is generated on the fly with WebAudio oscillators and a noise
   buffer, so there are no audio files to ship and nothing extra to load inside
   the activity iframe.

   Muted by default (this plays inside voice channels — see SettingsContext).
   The AudioContext is created lazily on the first play, which by construction
   only happens after the user has clicked the sound toggle, satisfying the
   browser autoplay gesture requirement.
   ========================================================================== */

let ctx = null;
let master = null;
let enabled = false;

const MASTER_GAIN = 0.22;

function getContext() {
  if (ctx) return ctx;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;

  try {
    ctx = new AudioCtx();
    master = ctx.createGain();
    master.gain.value = MASTER_GAIN;
    master.connect(ctx.destination);
  } catch (err) {
    console.warn('[sfx] WebAudio unavailable:', err);
    ctx = null;
  }
  return ctx;
}

/* A short burst of white noise — the raw material for statics and impacts. */
let noiseBuffer = null;
function getNoiseBuffer(audio) {
  if (noiseBuffer) return noiseBuffer;
  const length = audio.sampleRate * 0.5;
  noiseBuffer = audio.createBuffer(1, length, audio.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

/**
 * One synth voice: an oscillator swept from `freq` to `endFreq` with a
 * percussive attack/decay envelope.
 */
function tone(audio, {
  freq,
  endFreq = freq,
  type = 'square',
  start = 0,
  duration = 0.12,
  gain = 0.5,
  attack = 0.004,
}) {
  const t0 = audio.currentTime + start;
  const osc = audio.createOscillator();
  const env = audio.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (endFreq !== freq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(endFreq, 1), t0 + duration);
  }

  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  osc.connect(env);
  env.connect(master);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

/** Filtered noise burst — used for statics, thunks and confetti pops. */
function noise(audio, {
  start = 0,
  duration = 0.12,
  gain = 0.3,
  frequency = 1200,
  q = 1,
  type = 'bandpass',
}) {
  const t0 = audio.currentTime + start;
  const src = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const env = audio.createGain();

  src.buffer = getNoiseBuffer(audio);
  filter.type = type;
  filter.frequency.value = frequency;
  filter.Q.value = q;

  env.gain.setValueAtTime(gain, t0);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

  src.connect(filter);
  filter.connect(env);
  env.connect(master);
  src.start(t0);
  src.stop(t0 + duration + 0.02);
}

/* ── The kit ──────────────────────────────────────────────────────────────── */

const VOICES = {
  /* Menu tick on hover — deliberately almost subliminal. */
  hover: (a) => tone(a, { freq: 1400, type: 'square', duration: 0.03, gain: 0.12 }),

  /* Chunky button press. */
  click: (a) => {
    tone(a, { freq: 320, endFreq: 150, type: 'square', duration: 0.09, gain: 0.35 });
    noise(a, { duration: 0.05, gain: 0.18, frequency: 900, q: 0.7 });
  },

  /* Cartridge inserted / language chosen. */
  select: (a) => {
    tone(a, { freq: 300, endFreq: 900, type: 'square', duration: 0.1, gain: 0.3 });
    tone(a, { freq: 600, endFreq: 1200, type: 'triangle', start: 0.07, duration: 0.16, gain: 0.28 });
  },

  /* Guess hit: bright two-note rise. */
  correct: (a) => {
    tone(a, { freq: 660, type: 'square', duration: 0.08, gain: 0.34 });
    tone(a, { freq: 990, type: 'square', start: 0.06, duration: 0.12, gain: 0.3 });
    tone(a, { freq: 1320, type: 'triangle', start: 0.11, duration: 0.16, gain: 0.22 });
  },

  /* Guess miss: detuned drop plus a hit of tape static. */
  wrong: (a) => {
    tone(a, { freq: 220, endFreq: 90, type: 'sawtooth', duration: 0.2, gain: 0.3 });
    noise(a, { duration: 0.14, gain: 0.22, frequency: 400, q: 0.5 });
  },

  /* Already guessed: dull, unsatisfying thud. */
  duplicate: (a) => {
    tone(a, { freq: 180, endFreq: 140, type: 'sine', duration: 0.14, gain: 0.3 });
  },

  /* Close guess: warm two-note nudge, brighter than a miss but short of a hit. */
  close: (a) => {
    tone(a, { freq: 440, endFreq: 520, type: 'triangle', duration: 0.12, gain: 0.28 });
    tone(a, { freq: 660, type: 'triangle', start: 0.08, duration: 0.1, gain: 0.18 });
  },

  /* A word popping into view in the article body. */
  reveal: (a) => tone(a, { freq: 1200, endFreq: 1800, type: 'triangle', duration: 0.06, gain: 0.14 }),

  /* Victory fanfare — major arpeggio with a shimmer tail. */
  win: (a) => {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    notes.forEach((freq, i) => {
      tone(a, { freq, type: 'square', start: i * 0.09, duration: 0.22, gain: 0.3 });
      tone(a, { freq: freq * 2, type: 'triangle', start: i * 0.09, duration: 0.3, gain: 0.12 });
    });
    tone(a, { freq: 1046.5, type: 'square', start: 0.52, duration: 0.6, gain: 0.32 });
    tone(a, { freq: 1567.98, type: 'triangle', start: 0.52, duration: 0.7, gain: 0.2 });
    noise(a, { start: 0.5, duration: 0.5, gain: 0.1, frequency: 5000, q: 0.4 });
  },

  /* CRT powering on, played once when the game stage mounts. */
  powerOn: (a) => {
    tone(a, { freq: 60, endFreq: 4000, type: 'sine', duration: 0.35, gain: 0.16 });
    noise(a, { start: 0.05, duration: 0.3, gain: 0.12, frequency: 8000, q: 0.3, type: 'highpass' });
  },
};

export const sfx = {
  setEnabled(next) {
    enabled = next;
    if (next) {
      const audio = getContext();
      // Browsers hand back a suspended context when it was created outside a
      // gesture; resuming here (we are inside the toggle's click) unblocks it.
      if (audio?.state === 'suspended') audio.resume().catch(() => {});
    }
  },

  play(name) {
    if (!enabled) return;
    const voice = VOICES[name];
    if (!voice) return;

    const audio = getContext();
    if (!audio) return;
    if (audio.state === 'suspended') audio.resume().catch(() => {});

    try {
      voice(audio);
    } catch (err) {
      console.warn(`[sfx] failed to play "${name}":`, err);
    }
  },
};

export default sfx;
